import { CLINIC_ON } from '../edition.js';
import { LOOK_CHANGED } from '../look.js';
import {
  assistantHistorySchema,
  assistantStreamEventSchema,
  assistantTurnSchema,
  type AssistantStreamEvent,
  type AssistantTurn,
} from '@secretario/shared';
import type { ProxyTag } from '../replySignature.js';
import { askNotifications, markFirstDelivery } from '../firstRun.js';
import { apiFetch, apiStream } from './http.js';

export async function getAssistantHistory(): Promise<AssistantTurn[]> {
  return assistantHistorySchema.parse(await apiFetch<unknown>('/v1/assistant/history')).turns;
}

export function clearAssistantHistory(): Promise<void> {
  return apiFetch<void>('/v1/assistant/history', { method: 'DELETE' });
}

/** Envia um pedido ao Secretário e recebe atividades e texto conforme acontecem. */
export function streamAssistantMessage(
  text: string,
  onEvent: (event: AssistantStreamEvent) => void,
  signal?: AbortSignal,
  /** Anexos já enviados (ids), até 4. */
  attachments: string[] = [],
  /** O balão da clínica diz de onde vem: "abre a agenda" lá é a agenda da clínica. */
  place?: 'pessoal' | 'clinica',
  /** O lugar do Perto de que se fala (a unidade exata de uma rede). */
  lugar?: string,
): Promise<void> {
  // Um "obrigado" conta para o personagem (terceiro do dia: ele fica sem graça).
  if (/\bobrigad[oa]\b|\bvaleu\b/i.test(text)) {
    window.dispatchEvent(new CustomEvent('secretario:obrigado'));
  }
  return apiStream(
    '/v1/assistant/messages',
    // `caps`: o que este app sabe mostrar (documentos pedidos pela conversa, os objetivos e o
    // visual vivo do personagem).
    {
      text,
      caps: [
        'docs',
        'goals',
        'look',
        'recipes',
        'hd',
        'links',
        'idiomas',
        'choices',
        ...(CLINIC_ON ? ['clinic'] : []),
      ],
      ...(attachments.length ? { attachments } : {}),
      ...(place ? { place } : {}),
      ...(lugar ? { lugar } : {}),
    },
    (data) => {
      const parsed = assistantStreamEventSchema.safeParse(data);
      if (!parsed.success) return;
      const event = parsed.data;
      if (event.type === 'done') markFirstDelivery();
      if (
        event.type === 'activity' &&
        event.activity.tool === 'criar_lembrete' &&
        event.activity.state === 'done'
      ) {
        askNotifications(
          'Pra eu te lembrar na hora certa, preciso poder te mandar notificações. Elas nunca mostram o texto das suas conversas.',
        );
      }
      if (
        event.type === 'activity' &&
        event.activity.tool === 'cuidar_do_visual' &&
        event.activity.state === 'done'
      ) {
        window.dispatchEvent(new CustomEvent(LOOK_CHANGED));
      }
      // "Desconecta da TV" escrito: além de tirar da tela, encerra o espelhamento do celular.
      if (
        event.type === 'activity' &&
        event.activity.tool === 'tirar_da_tela' &&
        event.activity.state === 'done'
      ) {
        void import('../screenCast.js').then((cast) => cast.dismissTv());
      }
      onEvent(event);
    },
    signal,
  );
}

/** Pede ao Secretário uma fala para uma conversa entre pessoas, com o trecho recente dela. */
export function roomAssist(
  request: string,
  messages: { from: string; mine: boolean; text: string }[],
): Promise<{ text: string }> {
  return apiFetch<{ text: string }>('/v1/assistant/room', {
    method: 'POST',
    body: { request, messages },
  });
}

export type AutoReplyResult =
  | { action: 'reply'; text: string }
  | { action: 'approval'; approvalId: string }
  | { action: 'none'; reason: string };

/** Pergunta ao Secretário o que fazer com uma mensagem que chegou numa conversa em que ele está. */
export function autoReply(input: {
  roomId: string;
  eventId: string;
  roomKind: 'direct' | 'group';
  trigger: 'message' | 'call';
  from: { username: string; name: string };
  incoming: string;
  messages: { from: string; mine: boolean; text: string }[];
  explicit: boolean;
}): Promise<AutoReplyResult> {
  return apiFetch<AutoReplyResult>('/v1/assistant/auto-reply', { method: 'POST', body: input });
}

// ── Resposta por procuração ────────────────────────────────────────────────

export type ProxyStatusResult = {
  secretary: {
    name: string;
    characterKey: string;
    ownerName: string;
    business?: string | null;
  } | null;
  consent: 'granted' | 'declined' | 'unset';
};

/** O Secretário de quem está do outro lado responde nesta conversa? E eu já autorizei? */
export function getProxyStatus(roomId: string): Promise<ProxyStatusResult> {
  return apiFetch<ProxyStatusResult>(
    `/v1/assistant/proxy/status?roomId=${encodeURIComponent(roomId)}`,
  );
}

/** Autoriza (ou não) o Secretário da outra pessoa a ler minhas mensagens nesta conversa. */
export function setProxyConsent(roomId: string, granted: boolean): Promise<void> {
  return apiFetch<void>('/v1/assistant/proxy/consent', {
    method: 'PUT',
    body: { roomId, granted },
  });
}

export type OnBehalfResult =
  { action: 'reply'; text: string; tag: ProxyTag } | { action: 'none'; retryInMs?: number };

/** Entrega a mensagem que acabei de enviar ao Secretário de quem a recebe. */
export function onBehalfReply(input: {
  roomId: string;
  eventId: string;
  incoming: string;
  messages: { who: 'me' | 'secretary'; text: string }[];
}): Promise<OnBehalfResult> {
  return apiFetch<OnBehalfResult>('/v1/assistant/auto-reply/on-behalf', {
    method: 'POST',
    body: input,
  });
}

export type PendingProxyReply = {
  roomId: string;
  eventId: string;
  text: string;
  tag: ProxyTag | null;
};

export async function pendingProxyReplies(): Promise<PendingProxyReply[]> {
  const result = await apiFetch<{ replies: PendingProxyReply[] }>('/v1/assistant/proxy/pending');
  return result.replies;
}

export function proxyPosted(eventId: string): Promise<void> {
  return apiFetch<void>('/v1/assistant/proxy/posted', { method: 'POST', body: { eventId } });
}

/** Avisa que estou com o app aberto (ou que saí). */
export function sendPresence(here: boolean): Promise<void> {
  return apiFetch<void>('/v1/assistant/presence', {
    method: 'POST',
    body: { here },
    keepalive: !here,
  });
}

/** O toque em Sim ou Mudar numa ação que o Secretário deixou esperando (F0). */
export async function decideAction(
  id: string,
  decision: 'sim' | 'nao',
): Promise<{ state: string; turn: AssistantTurn | null }> {
  const data = await apiFetch<{ state: string; turn: unknown }>(
    `/v1/assistant/actions/${encodeURIComponent(id)}`,
    { method: 'POST', body: { decision } },
  );
  return {
    state: data.state,
    turn: data.turn ? assistantTurnSchema.parse(data.turn) : null,
  };
}
